'use client';

import type { CSSProperties, ReactNode } from 'react';
import { sizing } from '@galapagos/design-system/sizing';
import {
  spacing,
  SCREEN_GUTTER_WIDE_KEY,
  type SpacingToken,
} from '@galapagos/design-system/spacing';

export const PAGE_COLUMN_CLASS = 'page-column';
export const PAGE_COLUMN_INNER_CLASS = 'page-column__inner';

export interface PageColumnProps {
  children: ReactNode;
  /** Row gap between the column's children. */
  gap?: SpacingToken;
  /** Vertical padding inside the column, both edges. */
  paddingBlock?: SpacingToken;
  /** Top padding alone, for a shell whose bottom edge is owned by its footer. */
  paddingTop?: SpacingToken;
  /**
   * Lets the column fill a flex host (the page shells pass it so a short step
   * still pushes the site footer to the bottom). Applied to both the root and
   * the inner column; never set by default, so placement stays the host's.
   */
  grow?: boolean;
  /**
   * How wide the column may grow: `default` for steps and forms, `reading` for
   * long-form text, which needs a longer line than a form does.
   */
  width?: 'default' | 'reading';
  /**
   * When set, the root becomes a labelled `<section>` landmark. Left unset,
   * the root is a plain layout `div` so it never shadows the host page's
   * `<main>`.
   */
  'aria-label'?: string;
  'data-testid'?: string;
}

/**
 * The page's single content column: full width on a phone, and never wider
 * than `sizing.column.max`, centred by the root's flex alignment so the root
 * itself carries no margin. The screen-edge gutter is the root's horizontal
 * padding, so it sits outside the width cap and a desktop column keeps its
 * full measure. Web pages take the wide gutter, because their top-level
 * content is uncarded text and form controls (ADR-091 Rule 2). App layout
 * composition, not a design-system component (ADR-012 Rule 3); every value
 * it reads is a design-system element.
 */
export function PageColumn({
  children,
  gap,
  paddingBlock,
  paddingTop,
  grow = false,
  width = 'default',
  'aria-label': ariaLabel,
  'data-testid': testId,
}: PageColumnProps) {
  const rootStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    boxSizing: 'border-box',
    paddingLeft: spacing[SCREEN_GUTTER_WIDE_KEY],
    paddingRight: spacing[SCREEN_GUTTER_WIDE_KEY],
    ...(grow ? { flex: 1 } : {}),
  };

  const innerStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: width === 'reading' ? sizing.column.reading : sizing.column.max,
    boxSizing: 'border-box',
    ...(gap !== undefined ? { rowGap: spacing[gap] } : {}),
    ...(paddingBlock !== undefined
      ? { paddingTop: spacing[paddingBlock], paddingBottom: spacing[paddingBlock] }
      : {}),
    ...(paddingTop !== undefined ? { paddingTop: spacing[paddingTop] } : {}),
    ...(grow ? { flex: 1 } : {}),
  };

  const inner = (
    <div className={PAGE_COLUMN_INNER_CLASS} style={innerStyle}>
      {children}
    </div>
  );

  if (ariaLabel) {
    return (
      <section
        className={PAGE_COLUMN_CLASS}
        style={rootStyle}
        aria-label={ariaLabel}
        data-testid={testId}
      >
        {inner}
      </section>
    );
  }

  return (
    <div className={PAGE_COLUMN_CLASS} style={rootStyle} data-testid={testId}>
      {inner}
    </div>
  );
}
